'use client';

import { Mail, Phone } from 'lucide-react';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import type { BlockRenderProps } from '../../types';
import { ContactMessage, ContactPicture } from './contactChrome';
import { readContactView } from './contactView';

/**
 * Desk: the message is the section. Call, email, and today's hours are a
 * thin strip. The address sits on the photo, not in its own column.
 */
export function ContactDesk({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const view = readContactView({ block, locale, isActive, activeEditTarget, editable });
  const { target } = view;

  return (
    <section
      className={['ws-contact-desk', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-contact-arrangement="desk"
      id="visit"
    >
      <div className="ws-contact-desk__inner">
        <header className="ws-contact-desk__header">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-contact-desk__title"
          >
            {view.sectionTitle}
          </EditableContent>
          {view.intro || editable ? (
            <EditableContent
              fieldKey="intro"
              editable={editable}
              activeEditTarget={target}
              className="ws-contact-desk__intro"
            >
              {view.intro}
            </EditableContent>
          ) : null}
        </header>

        <ContactMessage
          view={view}
          locale={locale}
          className="ws-contact__form-panel"
          titleClass="ws-contact__form-title"
          supportClass="ws-contact__form-support"
        />

        <ul className="ws-contact-desk__strip">
          {view.phone || editable ? (
            <li>
              {view.telHref && !editable ? (
                <a className="ws-contact-desk__action" href={view.telHref}>
                  <LucideIcon icon={Phone} size="sm" className="ws-contact__action-icon" />
                  <span>{view.phone}</span>
                </a>
              ) : (
                <EditableContent
                  as="span"
                  fieldKey="phone"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-contact-desk__action"
                >
                  {view.phone}
                </EditableContent>
              )}
            </li>
          ) : null}
          {view.email || editable ? (
            <li>
              {view.mailHref && !editable ? (
                <a className="ws-contact-desk__action" href={view.mailHref}>
                  <LucideIcon icon={Mail} size="sm" className="ws-contact__action-icon" />
                  <span>{view.email}</span>
                </a>
              ) : (
                <EditableContent
                  as="span"
                  fieldKey="email"
                  editable={editable}
                  activeEditTarget={target}
                  className="ws-contact-desk__action"
                >
                  {view.email}
                </EditableContent>
              )}
            </li>
          ) : null}
          {view.hoursLine || editable ? (
            <li>
              <EditableContent
                fieldKey="hours"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-desk__today"
              >
                {view.hoursLine}
              </EditableContent>
            </li>
          ) : null}
        </ul>

        {view.showMap || view.address || editable ? (
          <div className="ws-contact-desk__place">
            {view.showMap ? (
              <ContactPicture
                view={view}
                className="ws-contact-desk__visual"
                photoClass="ws-contact__photo"
              />
            ) : null}
            {view.address || editable ? (
              <EditableContent
                fieldKey="address"
                editable={editable}
                activeEditTarget={target}
                className="ws-contact-desk__address"
              >
                {view.address}
              </EditableContent>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
